import {
  Download,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Plus,
  Search,
  Table as TableIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv, type ApiError } from '@/core/api';
import { useEmployees } from '@/features/hrm/employees/queries';
import { employeeName } from '@/features/hrm/employees/types';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { BASE as ONBOARDING_BASE } from '../api';
import {
  ONBOARDING_KINDS,
  ONBOARDING_STATUSES,
  onboardingKindLabel,
  onboardingStatusLabel,
  onboardingStatusVariant,
  type OnboardingKind,
  type OnboardingStatus,
} from '../constants';
import { useOnboardings } from '../queries';
import type { Onboarding } from '../types';
import { useOnboardingUi, type OnboardingViewMode } from '../store/viewPrefs';

const ALL = 'all';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type OnboardingOrderingColumn = 'status' | 'kind' | 'started_on' | 'completed_on';

const VIEW_MODES: readonly ViewModeOption<OnboardingViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by status', icon: Layers },
];

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : '—';
}

function progressLabel(o: Onboarding): string {
  const total = o.items.length;
  const done = o.items.filter((i) => i.is_done).length;
  if (total === 0) return 'No items';
  return `${done}/${total} done`;
}

export function OnboardingListPage() {
  const navigate = useNavigate();
  const view = useOnboardingUi((s) => s.view);
  const setView = useOnboardingUi((s) => s.setView);
  const ordering = useOnboardingUi((s) => s.ordering);
  const setOrdering = useOnboardingUi((s) => s.setOrdering);

  // Server-driven filters live in local state — pagination + search hit the API.
  const [searchInput, setSearchInput] = useState('');
  // Live, debounced search — fires 300ms after typing stops (no Search button).
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [status, setStatus] = useState<OnboardingStatus | typeof ALL>(ALL);
  const [kind, setKind] = useState<OnboardingKind | typeof ALL>(ALL);
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = useOnboardings({
    page,
    search: search || undefined,
    status: status === ALL ? undefined : status,
    kind: kind === ALL ? undefined : kind,
    ordering,
  });

  const results = data?.results ?? [];

  // Resolve employee display names from the employees list (the onboarding
  // payload only carries the employee id).
  const { data: employeesData } = useEmployees({});
  const employeeLabels = useMemo(() => {
    const map = new Map<number, string>();
    for (const e of employeesData?.results ?? []) map.set(e.id, employeeName(e));
    return map;
  }, [employeesData]);
  const labelFor = (o: Onboarding) =>
    employeeLabels.get(o.employee) ?? `Employee #${o.employee}`;

  const open = (o: Onboarding) => navigate(`/hrm/onboarding/${o.public_id}`);

  const viewProps = { onboardings: results, labelFor, onOpen: open };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Onboarding</h1>
        <Button onClick={() => navigate('/hrm/onboarding/new')}>
          <Plus /> New checklist
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search by employee…"
              className="pl-8"
              aria-label="Search onboarding checklists"
            />
          </div>
          <Select
            value={status}
            onValueChange={(v) => {
              setPage(1);
              setStatus(v as OnboardingStatus | typeof ALL);
            }}
          >
            <SelectTrigger className="w-44" aria-label="Filter by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              {ONBOARDING_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={kind}
            onValueChange={(v) => {
              setPage(1);
              setKind(v as OnboardingKind | typeof ALL);
            }}
          >
            <SelectTrigger className="w-44" aria-label="Filter by kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All kinds</SelectItem>
              {ONBOARDING_KINDS.map((k) => (
                <SelectItem key={k.value} value={k.value}>
                  {k.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `onboardings.csv` without leaving the SPA.
              void exportCsv(ONBOARDING_BASE, {
                search: search || undefined,
                status: status === ALL ? undefined : status,
                kind: kind === ALL ? undefined : kind,
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export onboarding checklists as CSV"
            title="Export onboarding checklists as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load checklists.'}
        </p>
      ) : isPending ? (
        <OnboardingSkeleton />
      ) : results.length === 0 ? (
        <OnboardingTable
          onboardings={[]}
          labelFor={labelFor}
          onOpen={open}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'table' ? (
        <OnboardingTable {...viewProps} ordering={ordering} onOrderingChange={setOrdering} />
      ) : view === 'list' ? (
        <OnboardingList {...viewProps} />
      ) : view === 'cards' ? (
        <OnboardingCards {...viewProps} />
      ) : (
        <OnboardingGroupedByStatus {...viewProps} />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'checklist' : 'checklists'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ViewProps {
  onboardings: Onboarding[];
  labelFor: (o: Onboarding) => string;
  onOpen: (o: Onboarding) => void;
}

interface OnboardingTableProps extends ViewProps {
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function StatusBadge({ status }: { status: OnboardingStatus }) {
  return (
    <Badge variant={onboardingStatusVariant(status)}>{onboardingStatusLabel(status)}</Badge>
  );
}

function OnboardingTable({
  onboardings,
  labelFor,
  onOpen,
  ordering,
  onOrderingChange,
}: OnboardingTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Employee</TableHead>
            <SortableTableHead<OnboardingOrderingColumn>
              column="kind"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden sm:table-cell"
            >
              Kind
            </SortableTableHead>
            <SortableTableHead<OnboardingOrderingColumn>
              column="status"
              value={ordering}
              onChange={onOrderingChange}
            >
              Status
            </SortableTableHead>
            <SortableTableHead<OnboardingOrderingColumn>
              column="started_on"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden md:table-cell"
            >
              Started
            </SortableTableHead>
            <SortableTableHead<OnboardingOrderingColumn>
              column="completed_on"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden lg:table-cell"
            >
              Completed
            </SortableTableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {onboardings.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                No checklists found.
              </TableCell>
            </TableRow>
          ) : (
            onboardings.map((o) => (
              <TableRow key={o.id} className="cursor-pointer" onClick={() => onOpen(o)}>
                <TableCell className="font-medium">{labelFor(o)}</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">
                  {onboardingKindLabel(o.kind)}
                </TableCell>
                <TableCell>
                  <StatusBadge status={o.status} />
                </TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {formatDate(o.started_on)}
                </TableCell>
                <TableCell className="text-muted-foreground hidden lg:table-cell">
                  {formatDate(o.completed_on)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function OnboardingCards({ onboardings, labelFor, onOpen }: ViewProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {onboardings.map((o) => (
        <Card
          key={o.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(o)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(o);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader>
            <CardTitle className="text-base">{labelFor(o)}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-xs">
            <StatusBadge status={o.status} />
            <span className="text-muted-foreground">{onboardingKindLabel(o.kind)}</span>
            <span className="text-muted-foreground ml-auto">{progressLabel(o)}</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function OnboardingList({ onboardings, labelFor, onOpen }: ViewProps) {
  return (
    <ul className="divide-y rounded-md border">
      {onboardings.map((o) => (
        <li key={o.id}>
          <button
            type="button"
            onClick={() => onOpen(o)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-semibold">{labelFor(o)}</span>
            <span className="text-muted-foreground hidden truncate text-xs sm:inline">
              {onboardingKindLabel(o.kind)}
            </span>
            <StatusBadge status={o.status} />
            <span className="text-muted-foreground text-xs">{progressLabel(o)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function OnboardingGroupedByStatus({ onboardings, labelFor, onOpen }: ViewProps) {
  // Bucket by status, preserving the declared status order; skip empty groups.
  const groups = useMemo(() => {
    return ONBOARDING_STATUSES.map((s) => ({
      key: s.value,
      label: s.label,
      bucket: onboardings.filter((o) => o.status === s.value),
    }));
  }, [onboardings]);

  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => {
        if (bucket.length === 0) return null;
        return (
          <section key={key} aria-labelledby={`onboarding-status-${key}`}>
            <h2
              id={`onboarding-status-${key}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
            </h2>
            <OnboardingCards onboardings={bucket} labelFor={labelFor} onOpen={onOpen} />
          </section>
        );
      })}
    </div>
  );
}

function OnboardingSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
